import type { AxiosError } from 'axios';

export interface ApiErrorItem {
  code?: string | null;
  message?: string | null;
  // Structured payload the BE attaches to the primary error item (BE `ErrorItem.Data`,
  // an `IReadOnlyDictionary<string,string>?`). Carries machine-readable follow-up data
  // that must NOT ride the human-readable `message` — e.g. the 2FA
  // `AUTH_2FA_REQUIRED` challenge token lives in `errors[0].data.challengeToken`.
  data?: Record<string, unknown> | null;
}

export interface ApiErrorPayload {
  // ActionResponse<T> envelope (the canonical shape from the .NET BE).
  isSuccessful?: boolean;
  // `errors` arrives in two shapes depending on where the BE caught the error:
  //  - house envelope (business errors): ApiErrorItem[]  → [{ code, message }]
  //  - ASP.NET ProblemDetails (model validation): { field: string[] }
  errors?: ApiErrorItem[] | Record<string, string[]> | null;
  message?: string | null;
  // RFC7807 ProblemDetails fields (returned by the validation pipeline).
  title?: string;
  // Legacy / problem-details adjacent fields kept for backwards compatibility
  // with MSW handlers and any non-envelope error responses.
  code?: string;
  details?: Record<string, unknown>;
  fieldErrors?: Record<string, string[]>;
}

export class NormalizedApiError extends Error {
  status: number;
  code: string;
  details?: Record<string, unknown>;
  fieldErrors?: Record<string, string[]>;
  // Structured data lifted from the envelope's primary error item (`errors[0].data`).
  // Callers read machine-readable follow-up state here — e.g. the 2FA challenge token
  // (`err.data?.challengeToken`) needed to POST `admin/login/2fa`.
  data?: Record<string, unknown>;
  isNetwork: boolean;

  constructor(params: {
    status: number;
    code: string;
    message: string;
    details?: Record<string, unknown>;
    fieldErrors?: Record<string, string[]>;
    data?: Record<string, unknown>;
    isNetwork?: boolean;
  }) {
    super(params.message);
    this.name = 'NormalizedApiError';
    this.status = params.status;
    this.code = params.code;
    this.details = params.details;
    this.fieldErrors = params.fieldErrors;
    this.data = params.data;
    this.isNetwork = params.isNetwork ?? false;
  }
}

export function normalizeAxiosError(err: unknown): NormalizedApiError {
  if (err instanceof NormalizedApiError) return err;
  const ax = err as AxiosError<ApiErrorPayload>;
  if (ax?.isAxiosError) {
    if (!ax.response) {
      return new NormalizedApiError({
        status: 0,
        code: ax.code ?? 'NETWORK_ERROR',
        message: 'Sunucuya ulaşılamadı. İnternet bağlantınızı kontrol edin.',
        isNetwork: true,
      });
    }
    const payload = ax.response.data ?? {};
    const status = ax.response.status;

    // `errors` may be the house envelope array [{code,message}] OR the
    // ASP.NET ProblemDetails validation dict { field: string[] }. Normalize
    // both so callers always get a human message + structured `fieldErrors`.
    const rawErrors = payload.errors;
    const envelopeError = Array.isArray(rawErrors) ? rawErrors[0] : undefined;
    const problemDetailsErrors =
      rawErrors && !Array.isArray(rawErrors) && typeof rawErrors === 'object'
        ? rawErrors
        : undefined;
    const firstFieldMessage = problemDetailsErrors
      ? Object.values(problemDetailsErrors).flat().find(Boolean)
      : undefined;

    // Prefer the envelope's `errors[0]` (BusinessRuleException / FluentValidation),
    // then the first ProblemDetails field message, then the envelope/problem
    // top-level text, then a per-status fallback.
    const code = envelopeError?.code ?? payload.code ?? `HTTP_${status}`;
    const message =
      envelopeError?.message ??
      firstFieldMessage ??
      payload.message ??
      payload.title ??
      defaultMessageForStatus(status);
    return new NormalizedApiError({
      status,
      code,
      message,
      details: payload.details,
      fieldErrors: problemDetailsErrors ?? payload.fieldErrors,
      data: envelopeError?.data ?? undefined,
    });
  }
  const message = err instanceof Error ? err.message : 'Bilinmeyen hata';
  return new NormalizedApiError({ status: -1, code: 'UNKNOWN', message });
}

export function toErrorMessage(err: unknown, fallback = 'İşlem başarısız'): string {
  if (err instanceof NormalizedApiError) return err.message;
  if (err instanceof Error) return err.message || fallback;
  return fallback;
}

function defaultMessageForStatus(status: number): string {
  switch (status) {
    case 400:
      return 'Geçersiz istek.';
    case 401:
      return 'Yetkisiz erişim. Lütfen yeniden giriş yapın.';
    case 403:
      return 'Bu işlem için yetkiniz yok.';
    case 404:
      return 'Kayıt bulunamadı.';
    case 409:
      return 'Çakışma: bu kayıt başkası tarafından değiştirilmiş olabilir.';
    case 422:
      return 'Doğrulama hatası.';
    case 429:
      return 'Çok fazla istek. Lütfen biraz bekleyin.';
    case 500:
    case 502:
    case 503:
    case 504:
      return 'Sunucu hatası. Lütfen tekrar deneyin.';
    default:
      return 'İstek başarısız oldu.';
  }
}
